import { useEffect, useState } from "react"
import { useAppStore } from "@/hooks/useAppStore"
import { getHealth, getDashboardSummary } from "@/services/api"
import { Panel, Stat, StatGrid, Badge, StatusIndicator, SectionTitle } from "@/components/ui"
import { Trajectory3D } from "@/components/viewer/Trajectory3D"
import { SimCharts } from "@/components/charts/TelemetryCharts"
import { TelemetryReadout } from "@/components/ui/TelemetryReadout"
import { formatTime } from "@/lib/format"

export default function OverviewPage() {
  const {
    simStatus,
    simTelemetry,
    simCurrent,
  } = useAppStore()
  const [health, setHealth] = useState<"online" | "offline">("offline")
  const [summary, setSummary] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const [h, s] = await Promise.all([getHealth(), getDashboardSummary()])
        if (!cancelled) {
          setHealth(h.data.status === "ok" ? "online" : "offline")
          setSummary(s.data)
        }
      } catch (e) {
        if (!cancelled) setHealth("offline")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-muted">
        <div className="flex h-8 w-8 rounded-full border-2 border-border border-t-cyan-400 animate-spin mb-3" />
        <p>Initializing mission control&hellip;</p>
      </div>
    )
  }

  const s = summary || {}
  const recent = (s.recent_runs || []).slice(0, 6)

  const successRate = s.landing_success_rate ?? 0

  const simActive = simStatus === "running" || simStatus === "paused"
  const simPoints = simTelemetry || []
  const liveReading = simCurrent

  const distanceToTarget = liveReading
    ? Math.sqrt(liveReading.x * liveReading.x + liveReading.y * liveReading.y)
    : null

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Mission Control</h1>
          <p className="text-muted text-sm">
            AeroRocket telemetry &mdash; live simulation and flight data
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusIndicator
            status={health === "online" ? "online" : "offline"}
            label={health === "online" ? "API Online" : "API Offline"}
          />
          <Badge variant={simActive ? "running" : "nominal"}>
            {simStatus.toUpperCase()}
          </Badge>
        </div>
      </div>

      <SectionTitle>Mission status</SectionTitle>
      <StatGrid cols={5}>
        <Stat label="Vehicle" value="AeroRocket-1" status="nominal" accent="blue" />
        <Stat label="Flight phase" value={simActive ? "Ascending" : "Idle"} status={simActive ? "running" : "muted"} accent="cyan" />
        <Stat label="Mission time" value={formatTime(liveReading?.t ?? 0)} status="nominal" accent="cyan" />
        <Stat label="Landing success rate" value={successRate.toFixed(1)} unit="%" status={successRate >= 70 ? "safe" : "warn"} accent="amber" />
        <Stat label="Total runs" value={s.total_runs ?? 0} status="nominal" accent="cyan" />
      </StatGrid>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7 xl:col-span-8 gap-4">
          <Panel title="Flight trajectory" padded={false}>
            <div className="p-3">
              <Trajectory3D
                points={simPoints.map((p) => ({
                  t: p.t,
                  x: p.x,
                  z: p.y,
                  y: p.altitude,
                  speed: p.speed,
                  fuel: p.fuel,
                  throttle: p.throttle,
                  altitude: p.altitude,
                  vx: p.vx,
                  vy: p.vy,
                  vz: p.vz,
                  tilt: p.tilt,
                }))}
                height={430}
                playing={simActive}
              />
            </div>
          </Panel>

          <Panel title="Live telemetry" padded={false}>
            <div className="p-3">
              <TelemetryReadout reading={liveReading ?? null} />
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Stat label="Distance to target" value={distanceToTarget?.toFixed(1) ?? "—"} unit="m" status="nominal" accent="cyan" />
                <Stat label="Landing error" value="—" unit="m" status="muted" accent="cyan" />
              </div>
              {!liveReading && (
                <div className="mt-3 h-10 flex items-center justify-center text-muted text-sm">
                  Start a simulation in Flight Simulation to stream live telemetry.
                </div>
              )}
            </div>
          </Panel>
        </div>

        <div className="lg:col-span-5 xl:col-span-4 gap-4">
          <Panel title="Recent runs" padded={false}>
            <div className="p-3">
              {recent.length === 0 ? (
                <p className="text-muted text-sm">No runs recorded yet.</p>
              ) : (
                <div className="space-y-2">
                  {recent.map((r: any) => {
                    const m = r.metrics || {}
                    const ok = m.landing_success === true
                    return (
                      <div
                        key={r.id}
                        className="flex items-center gap-2.5 p-2 rounded-sm border border-transparent hover:border-border hover:bg-surface/50 transition"
                      >
                        <Badge variant={ok ? "safe" : "danger"}>
                          {ok ? "LANDED" : "IMPACT"}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-ink truncate">{r.algorithm.toUpperCase()}</div>
                          <div className="text-[0.625rem] text-dim truncate">
                            {r.obs_mode ?? "n/a"} obs &middot; seed {r.seed ?? "—"}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs font-mono text-muted">
                            {m.touchdown_velocity_mps?.toFixed?.(1) ?? "—"} m/s
                          </div>
                          <div className="text-[0.625rem] text-dim">{r.status}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </Panel>

          <Panel title="Mission metrics" padded={false}>
            <div className="p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[0.625rem] text-muted">Total experiments</span>
                <span className="text-sm font-mono text-ink">{s.total_experiments ?? 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[0.625rem] text-muted">Total runs</span>
                <span className="text-sm font-mono text-ink">{s.total_runs ?? 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[0.625rem] text-muted">Avg. landing rate</span>
                <span className="text-sm font-mono text-ink">{successRate.toFixed(1)}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[0.625rem] text-muted">Best touchdown velocity</span>
                <span className="text-sm font-mono text-ink">
                  {s.best_touchdown_velocity?.toFixed?.(1) ?? "—"} m/s
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[0.625rem] text-muted">Active workers</span>
                <span className="text-sm font-mono text-ink">{s.active_workers ?? 0}</span>
              </div>
            </div>
          </Panel>
        </div>
      </div>

      <SectionTitle>Flight telemetry charts</SectionTitle>
      {simPoints.length > 0 ? (
        <SimCharts telemetry={simPoints as any} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {["Altitude", "Velocity", "Fuel", "Acceleration"].map((t) => (
            <div
              key={t}
              className="panel p-3 h-[250px] flex flex-col items-center justify-center text-muted text-sm"
            >
              No live telemetry &mdash; run a simulation
            </div>
          ))}
        </div>
      )}
    </div>
  )
}


